/**
 * ProjectileAimResolver — single source of truth for projectile direction.
 *
 * Before this resolver, three call sites all baked their own "facing
 * vs. target" logic:
 *   - ProjectileBehavior (local cast)
 *   - EntitySystem.handleRemoteAbilityProjectile (remote cast)
 *   - ProjectileSystem.createProjectile internals (init vx/vy)
 *
 * Each path picked a slightly different fallback when target was set
 * but homing was 0, which produced the "sometimes target wins, sometimes
 * facing wins" feel DJ flagged 2026-04-30. This module collapses those
 * three paths to one and answers ONE question: given a caster and an
 * optional target, what initial velocity vector should the projectile
 * fire with?
 *
 * Rule (canonical):
 *   1. If a live target position resolves → aim at target (vector toward it).
 *   2. Else if facing is known → fire flat in that direction (left/right).
 *   3. Else → default to right (sane fallback, never 0).
 *
 * Homing is independent — it engages on top of this initial aim once the
 * homing delay elapses. Without homing, the projectile flies a straight
 * line at the resolved aim. This means a non-homing fireball with a
 * target will still hit a stationary target reliably, which was the user
 * intent that the legacy facing-only path broke.
 */

import { ServiceRegistry } from '../../core/ServiceRegistry';
import { playerCombatPoint, playerReachBody } from '../../../shared/logic/CombatAnchors';
import { reachToBody, type ReachBody } from '../../../shared/logic/BodyReach';
import { DEFAULT_MUZZLE_OFFSET_X, DEFAULT_MUZZLE_OFFSET_Y } from '../../../shared/aiming/contract';
import { launchCasterWidth, muzzleSpawnX } from '../../../shared/aiming/launchGeometry';

interface WorldPoint {
  x: number;
  y: number;
}

export interface AimInput {
  /** Caster world position (sprite center). */
  casterX: number;
  casterY: number;
  /** Caster facing fallback when no target. */
  facing: 'left' | 'right';
  /** Optional target id. Resolved against EntitySystem registries. */
  targetId?: string | null;
  /** Pixel-per-second projectile speed. */
  speed: number;
}

export interface AimOutput {
  /** Initial velocity x (px/s). */
  vx: number;
  /** Initial velocity y (px/s). Zero only when falling back to flat facing. */
  vy: number;
  /** Discrete L/R direction the resolver settled on (visual flip + back-compat). */
  direction: 'left' | 'right';
  /** Whether a target position was actually resolved (for diagnostics). */
  targetResolved: boolean;
  /** Resolved target position when present (null when free-aim). */
  targetX: number | null;
  targetY: number | null;
}

export interface ProjectileLaunchInput {
  /** Live caster body. `position` is the physics top-left. */
  caster: {
    position: WorldPoint;
    width?: number;
    height?: number;
    facing?: string;
  };
  /** Optional live target id. */
  targetId?: string | null;
  /** Pixel-per-second projectile speed. */
  speed: number;
  /** Render/collision dimensions used to find the actual projectile center. */
  projectileWidth: number;
  projectileHeight: number;
  /** Right-facing muzzle offset from the caster top-left (left mirrors it about the caster centre). */
  spawnOffsetX?: number;
  spawnOffsetY?: number;
}

export interface ProjectileLaunchOutput extends AimOutput {
  /** Projectile top-left spawn coordinates. */
  spawnX: number;
  spawnY: number;
}

/**
 * Resolve initial projectile aim. Pure function modulo the
 * EntitySystem lookup; safe to call from any thread (single-threaded
 * browser) on a per-cast basis.
 */
export function resolveProjectileAim(input: AimInput): AimOutput {
  const targetPos = input.targetId ? resolveTargetPosition(input.targetId) : null;
  return resolveAimAgainstPosition(input, targetPos);
}

/**
 * Resolve the complete single-projectile launch plan.
 *
 * Muzzle placement is the shared `muzzleSpawnX` (caster top-left +40, -5
 * facing right; the mirror image about the caster centre facing left), so a
 * client spawn matches the server's `resolveAssistedLaunch`. The
 * velocity vector, however, originates at the CENTER of the projectile that
 * will actually be spawned. This prevents thin projectiles from visually
 * leaving one point while collision travels along a vector derived from a
 * different point.
 */
export function resolveProjectileLaunch(input: ProjectileLaunchInput): ProjectileLaunchOutput {
  const targetPos = input.targetId ? resolveTargetPosition(input.targetId) : null;
  const fallbackFacing: 'left' | 'right' = input.caster.facing === 'right' ? 'right' : 'left';
  const casterCenter = playerCombatPoint(input.caster);

  const launchDirection: 'left' | 'right' = targetPos && targetPos.x !== casterCenter.x
    ? (targetPos.x > casterCenter.x ? 'right' : 'left')
    : fallbackFacing;
  const spawnX = muzzleSpawnX(input.caster.position.x, launchCasterWidth(input.caster.width),
    launchDirection, input.projectileWidth, input.spawnOffsetX ?? DEFAULT_MUZZLE_OFFSET_X);
  const spawnY = input.caster.position.y + (input.spawnOffsetY ?? DEFAULT_MUZZLE_OFFSET_Y);

  const aim = resolveAimAgainstPosition({
    casterX: spawnX + input.projectileWidth / 2,
    casterY: spawnY + input.projectileHeight / 2,
    facing: launchDirection,
    targetId: input.targetId,
    speed: input.speed,
  }, targetPos);

  return { ...aim, spawnX, spawnY };
}

function resolveAimAgainstPosition(input: AimInput, targetPos: WorldPoint | null): AimOutput {
  if (targetPos) {
    const dx = targetPos.x - input.casterX;
    const dy = targetPos.y - input.casterY;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len > 0.0001) {
      const nx = dx / len;
      const ny = dy / len;
      return {
        vx: nx * input.speed,
        vy: ny * input.speed,
        direction: nx >= 0 ? 'right' : 'left',
        targetResolved: true,
        targetX: targetPos.x,
        targetY: targetPos.y,
      };
    }
    // Caster on top of target — fall through to facing.
  }

  const isRight = input.facing === 'right';
  return {
    vx: isRight ? input.speed : -input.speed,
    vy: 0,
    direction: isRight ? 'right' : 'left',
    targetResolved: false,
    targetX: null,
    targetY: null,
  };
}

/**
 * Resolve a target id to a world-space position (body center).
 * Tries the live combat-mob registry first, then remote-player registry,
 * then local player. Returns null if the id can't be located.
 *
 * Exported as `resolveTargetPosition` for reuse by the cast-time
 * range gate in PlayerManager. Same lookup the projectile aim path
 * uses, so range checks and aim vectors agree on "where is the
 * target" by construction.
 *
 * Best-effort: if the relevant systems aren't on the registry yet (very
 * early boot), returns null instead of throwing — callers fall back to facing
 * (aim) or skip the gate (range).
 */
export function resolveTargetPosition(targetId: string): { x: number; y: number } | null {
  const body = resolveTargetBody(targetId);
  return body ? { x: body.x, y: body.y } : null;
}

/**
 * The same lookup as {@link resolveTargetPosition}, plus the target's hitbox
 * size: a centred body `BodyReach.reachToBody` measures to. A mob carries its
 * own `width`/`height` (none = a point body, as the server treats a sizeless
 * row); a player gets `playerReachBody` — exactly the bodies the server's
 * range gate (`CombatCastHandler`) measures to (plan 21 B17).
 */
export function resolveTargetBody(targetId: string): ReachBody | null {
  return lookupTargetBody(targetId);
}

/**
 * The client's cast and basic-attack range rule: the caster's combat point
 * (hitbox centre) to the nearest point of the target's body — the measure the
 * server's range gate uses, without its network-jitter allowance (the client
 * knows its own position exactly). Null when the target cannot be located.
 */
export function reachToTarget(
  caster: Parameters<typeof playerCombatPoint>[0],
  targetId: string,
): number | null {
  const body = lookupTargetBody(targetId);
  return body ? reachToBody(playerCombatPoint(caster), body) : null;
}

function lookupTargetBody(targetId: string): ReachBody | null {
  try {
    const registry = ServiceRegistry.getInstance();

    // Combat mobs live in MobSystem, not EntitySystem's NPCManager. Mob.x/y is
    // already the canonical sprite/body center (Mob.ts + CombatAnchors), while
    // mob.position is the physics top-left. Using x/y keeps projectile aim,
    // homing, range gates, and targeting on the same point.
    const mob = registry.get('MobSystem', { optional: true })
      ?.getManager?.()
      ?.getMob?.(targetId);
    if (mob && Number.isFinite(mob.x) && Number.isFinite(mob.y)) {
      return { x: mob.x, y: mob.y, width: mob.width, height: mob.height };
    }

    const entitySystem = registry.get('EntitySystem', { optional: true });
    if (!entitySystem) return null;

    // Remote player lookup
    const remoteMgr = entitySystem.getRemotePlayerManager?.();
    const others = remoteMgr?.getOtherPlayers?.();
    if (others && typeof others.get === 'function') {
      const remote = others.get(targetId);
      if (remote?.position &&
          Number.isFinite(remote.position.x) &&
          Number.isFinite(remote.position.y)) {
        return playerReachBody(remote);
      }
    }

    // Local player lookup
    const localMgr = entitySystem.getPlayerManager?.();
    const local = localMgr?.getPlayer?.();
    if (local && local.id === targetId && local.position &&
        Number.isFinite(local.position.x) &&
        Number.isFinite(local.position.y)) {
      return playerReachBody(local);
    }
  } catch {
    // Registry race — fall through to facing fallback.
  }
  return null;
}
